import React, { useState, useEffect } from "react";
import { Download, Share2, X, Smartphone } from "lucide-react";

const DISMISS_STORAGE_KEY = "pwa-install-dismissed";
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const setDismissed = () => {
  try {
    localStorage.setItem(
      DISMISS_STORAGE_KEY,
      JSON.stringify({ at: Date.now() })
    );
  } catch {}
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isStandalone() || wasDismissedRecently()) return;

    const isIOSUser = isIOS();
    setIsIOSDevice(isIOSUser);

    if (isIOSUser) {
      setShowBanner(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (isStandalone() || wasDismissedRecently()) setShowBanner(false);
  }, [mounted]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed();
    setShowBanner(false);
  };

  const handleIOSShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "Vendor Dashboard",
        url: window.location.href,
        text: "Vendor Dashboard - Manage your menus and orders",
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        // User cancelled or share failed; ignore
      }
    }
  };

  const canUseShare = typeof navigator !== "undefined" && !!navigator.share;

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[100] max-w-md mx-auto sm:left-auto sm:right-4 sm:max-w-sm">
      <div className="bg-white rounded-xl shadow-lg border border-secondary-200 p-4 flex items-start gap-3">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {isIOSDevice ? (
          <>
            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-primary-600" />
            </div>
            <div className="pr-6 flex-1 min-w-0">
              <p className="text-sm font-medium text-secondary-900">
                Install Vendor Dashboard
              </p>
              <p className="text-xs text-secondary-500 mt-0.5">
                Use Safari&apos;s <span className="font-medium text-secondary-700">Share</span> button
                (at the <span className="font-medium text-secondary-700">bottom of the screen</span>),
                then tap <span className="font-medium text-secondary-700">Add to Home Screen</span>.
              </p>
              {canUseShare && (
                <button
                  type="button"
                  onClick={handleIOSShare}
                  className="mt-2 btn-primary text-xs py-2 px-3 inline-flex items-center gap-1.5"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  Open Share menu
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Download className="h-5 w-5 text-primary-600" />
            </div>
            <div className="pr-6 flex-1 min-w-0">
              <p className="text-sm font-medium text-secondary-900">
                Install Vendor Dashboard
              </p>
              <p className="text-xs text-secondary-500 mt-0.5">
                Use the app from your home screen or desktop
              </p>
              <button
                type="button"
                onClick={handleInstallClick}
                className="mt-2 btn-primary text-xs py-2 px-3 inline-flex items-center gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Install app
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
